'use client';
import {useEffect, useState} from 'react';
import Image from 'next/image';

import useWindowSize from '@/hooks/useWindowSize';
import {cn} from '@/lib/utils';
import {VillageMedia} from '@/services/types';
import GalleryModalCarousel from './GalleryModalCarousel';

type Props = {
  villageMedia: VillageMedia[];
  lang: string;
  setOpenModalCarousel: (value: boolean) => void;
};

type VillageMediaWithSize = {
  sizeRef: string;
} & VillageMedia;

export default function GalleryModalImages({
  villageMedia,
  lang,
  setOpenModalCarousel,
}: Props) {
  const [carouselItem, setCarouselItem] = useState<number | null>(null);
  const [firstImages, setFirstImages] = useState<VillageMedia[]>([]);
  const [restOfImages, setRestOfImages] = useState<VillageMedia[]>([]);
  const [imagesWithSizeRef, setImagesWithSizeRef] = useState<
    VillageMediaWithSize[]
  >([]);
  const {width} = useWindowSize();
  const galleryMedia = villageMedia.filter(
    (file) => file.type === 'gallery' || file.type === 'cover',
  );

  function getImageSize(size: string) {
    if (size == 'big') {
      if (width > 1024) {
        return '1024px';
      }
      return `${width}px`;
    }
    if (size == 'medium') {
      if (width > 1024) {
        return '497px';
      }
      return `${width / 2}px`;
    }
    if (size == 'small') {
      if (width > 1024) {
        return '321px';
      }
      return `${width / 3}px`;
    }
  }

  function setImagesSizes(images: VillageMedia[]) {
    const newImagesWithSizeRef: VillageMediaWithSize[] = [];
    let itensToSlice = 0;

    width < 640 ? (itensToSlice = 3) : (itensToSlice = 6);

    for (let i = 0; i < images.length; i += itensToSlice) {
      const group = images.slice(i, i + itensToSlice);

      for (let j = 0; j < group.length; j++) {
        const item: VillageMediaWithSize = {...group[j], sizeRef: ''};

        if (j === 0) {
          item.sizeRef = 'big';
        } else if (j <= 2) {
          item.sizeRef = 'medium';
        } else {
          item.sizeRef = 'small';
        }

        newImagesWithSizeRef.push(item);
      }
    }
    setImagesWithSizeRef(newImagesWithSizeRef);
  }

  function getImageUrlFromFilter(image: VillageMedia) {
    const mediaFilters = image.mediaFilters;

    if (mediaFilters) {
      const smallImage = mediaFilters.find(({name}) => name === '480w_webp');
      const mediumImage = mediaFilters.find(({name}) => name === '767w_webp');
      const bigImage = mediaFilters.find(({name}) => name === '1280w_webp');
      const extraBigImage = mediaFilters.find(
        ({name}) => name === '1920w_webp' || name === '1920w',
      );
      if (extraBigImage) {
        return extraBigImage.url;
      }
      if (bigImage) {
        return bigImage.url;
      }
      if (mediumImage) {
        return mediumImage.url;
      }
      if (smallImage) {
        return smallImage.url;
      }
    }
  }

  useEffect(() => {
    if (width < 640) {
      setFirstImages(galleryMedia.slice(0, 2));
      setRestOfImages(galleryMedia.slice(2, galleryMedia.length));
    } else {
      setFirstImages(galleryMedia.slice(0, 3));
      setRestOfImages(galleryMedia.slice(3, galleryMedia.length));
    }
    // eslint-disable-next-line
  }, [width]);

  useEffect(() => {
    setImagesSizes(restOfImages);
    // eslint-disable-next-line
  }, [restOfImages, width]);

  return (
    <>
      {carouselItem !== null ? (
        <GalleryModalCarousel
          goToItem={carouselItem}
          medias={galleryMedia}
          handleClose={() => {
            setCarouselItem(null);
            setOpenModalCarousel(false);
          }}
          lang={lang}
        />
      ) : null}
      <div id="gallery-images" className="m-[-0.9375rem] flex flex-wrap">
        {firstImages.map((image, index) => {
          const filterUrl = getImageUrlFromFilter(image);

          const imageDescription =
            image?.translations?.filter(
              (translation) => translation.locale === lang,
            )[0]?.description || '';

          const imageSrc = filterUrl
            ? filterUrl
            : `${process.env.NEXT_PUBLIC_S3_URI}/thumbs/480w_webp/${image.path}${image.filename}`;

          return (
            <div
              onClick={() => {
                setCarouselItem(index);
                setOpenModalCarousel(true);
              }}
              key={index}
              className="h-auto m-[0.3125rem] md:m-[0.9375rem] w-[calc(50%-0.625rem)] md:w-[calc(33.33333%-1.875rem)] flex-[0_0_auto] hover:shadow-[0_10px_20px_0_rgba(0,0,0,.3)] transition-shadow duration-300 cursor-pointer">
              <Image
                src={imageSrc}
                alt={imageDescription}
                width={400}
                height={214}
                sizes="(min-width: 1280px) 80vw, (min-width: 767px) 90vw, (min-width: 480px) 100vw, 100vw"
              />
            </div>
          );
        })}
        {imagesWithSizeRef.map((image, index) => {
          const imageSize = getImageSize(image.sizeRef);
          const interval = width < 640 ? 2 : 3;

          const filterUrl = getImageUrlFromFilter(image);

          const shouldApplyStyle = filterUrl?.includes('480w_webp') ?? false;

          const imageDescription =
            image?.translations?.filter(
              (translation) => translation.locale === lang,
            )[0]?.description || '';

          const imageSrc = filterUrl
            ? filterUrl
            : `${process.env.NEXT_PUBLIC_S3_URI}/thumbs/1920w_webp/${image.path}${image.filename}`;

          return (
            <div
              onClick={() => {
                setCarouselItem(index + interval);
                setOpenModalCarousel(true);
              }}
              key={index}
              className={cn(
                'h-auto m-[0.3125rem] md:m-[0.9375rem]  flex-[0_0_auto] hover:shadow-[0_10px_20px_0_rgba(0,0,0,.3)] transition-shadow duration-300 cursor-pointer',
                {
                  'w-[calc(50%-0.625rem)] md:w-[calc(33.33333%-1.875rem)]':
                    image.sizeRef === 'small',
                  'w-[calc(50%-0.625rem)] md:w-[calc(50%-1.875rem)]':
                    image.sizeRef === 'medium',
                  'w-[calc(100%-0.625rem)] md:w-[calc(100%-1.875rem)]':
                    image.sizeRef === 'big',
                },
              )}>
              <Image
                src={imageSrc}
                alt={imageDescription}
                width={1024}
                className={cn('', {
                  'md:my-auto md:mx-0 md:w-full md:max-h-[1667px] md:max-w-[2500px] md:relative lg:bg-[50%] desktop:bg-[50%_50%] desktop:bg-cover desktop:top-1/2 desktop:-translate-y-1/2':
                    shouldApplyStyle,
                })}
                height={
                  image.sizeRef === 'small'
                    ? 214
                    : image.sizeRef === 'medium'
                    ? 330
                    : 680
                }
                sizes="(min-width: 1280px) 80vw, (min-width: 767px) 90vw, (min-width: 480px) 100vw, 100vw"
              />
            </div>
          );
        })}
      </div>
    </>
  );
}
